// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Financeiros/Caixa.tsx
// ════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import {
  Plus, TrendingUp, TrendingDown, Wallet, Search,
  ArrowUpCircle, ArrowDownCircle, CheckCircle2, Clock,
} from 'lucide-react';
import {
  useCaixaSaldo,
  useCaixaPeriodo,
  useCaixaRegistrar,
} from '@/features/caixa/hooks/useCaixa';
import type { CriarLancamentoCaixaRequest } from '@/features/caixa/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { usePermissoes } from '@/contexts/PermissoesContext';
import { FORMULARIOS } from '@/domain/formularios';

// ── Helpers ───────────────────────────────────────────────────────────────

function primeiroDiaMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function hoje(): string {
  return new Date().toISOString().split('T')[0];
}

// ── Componente ────────────────────────────────────────────────────────────

const TODOS = 'todos';

const Caixa = () => {
  const { temPermissao } = usePermissoes();
  const podeIncluir = temPermissao(FORMULARIOS.CAIXAS, 'incluir');

  // Filtros — caixa 'todos' (default) mostra a movimentação de TODOS os caixas,
  // em vez do antigo caixa fixo #1 (que escondia a movimentação de outros caixas).
  const [caixaSel,   setCaixaSel]   = useState<string>(TODOS);
  const [inicio,     setInicio]     = useState(primeiroDiaMes);
  const [fim,        setFim]        = useState(hoje);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog
  const [openForm, setOpenForm] = useState(false);

  // Form state
  const [debitoCredito, setDebitoCredito] = useState<'C' | 'D'>('C');
  const [pagRec,        setPagRec]        = useState<'P' | 'R'>('R');
  const [descricao,     setDescricao]     = useState('');
  const [valorStr,      setValorStr]      = useState('');
  const [historico,     setHistorico]     = useState('');
  const [formCaixaId,   setFormCaixaId]   = useState('1');

  const caixaIdNum = caixaSel === TODOS ? null : Number(caixaSel);

  // Hooks
  const { data: saldoData } = useCaixaSaldo(caixaIdNum ?? 0);
  const { data: lancamentos = [], isLoading } = useCaixaPeriodo(inicio, fim);
  const registrar = useCaixaRegistrar();

  // Caixas para o seletor: os distintos presentes no período (+ o selecionado).
  const caixasDisponiveis = useMemo(() => {
    const ids = new Set<number>(lancamentos.map((l) => l.caixaId));
    if (caixaIdNum != null) ids.add(caixaIdNum);
    return Array.from(ids).sort((a, b) => a - b);
  }, [lancamentos, caixaIdNum]);

  // Filtrar lançamentos pelo caixa selecionado (ou todos) + busca
  const lancamentosFiltrados = useMemo(() => lancamentos
    .filter((l) => caixaIdNum == null || l.caixaId === caixaIdNum)
    .filter((l) =>
      l.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (l.historico ?? '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.dtMovimento).getTime() - new Date(a.dtMovimento).getTime()),
    [lancamentos, caixaIdNum, searchTerm]);

  // KPIs
  const totalCreditos = lancamentosFiltrados
    .filter((l) => l.debitoCredito === 'C')
    .reduce((s, l) => s + l.valor, 0);
  const totalDebitos = lancamentosFiltrados
    .filter((l) => l.debitoCredito === 'D')
    .reduce((s, l) => s + l.valor, 0);
  const totalConciliados = lancamentosFiltrados.filter((l) => l.isConciliado).length;

  // Saldo do card: caixa específico → saldo real da API; 'Todos' → resultado do período.
  const saldoPeriodo  = totalCreditos - totalDebitos;
  const saldoExibido  = caixaIdNum != null ? (saldoData?.saldo ?? 0) : saldoPeriodo;
  const saldoLabel    = caixaIdNum != null ? `Caixa #${caixaIdNum}` : 'Resultado do período (todos)';

  // Abrir dialog de novo lançamento — precisa de um caixa concreto.
  function abrirNovo() {
    const def = caixaIdNum ?? caixasDisponiveis[0] ?? 1;
    setFormCaixaId(String(def));
    setOpenForm(true);
  }

  // Submit
  const handleSubmit = () => {
    const valor = parseFloat(valorStr.replace(',', '.'));
    const cxId  = parseInt(formCaixaId, 10);
    if (!descricao.trim() || isNaN(valor) || valor <= 0 || isNaN(cxId) || cxId <= 0) return;

    const request: CriarLancamentoCaixaRequest = {
      caixaId: cxId,
      debitoCredito,
      pagRec,
      descricao: descricao.trim(),
      valor,
      historico: historico.trim() || undefined,
    };

    registrar.mutate(request, {
      onSuccess: () => {
        setOpenForm(false);
        setDescricao('');
        setValorStr('');
        setHistorico('');
      },
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Caixa</h1>
          <p className="text-muted-foreground mt-1">Movimentação financeira do caixa</p>
        </div>
        {podeIncluir && (
          <Button className="gap-2" onClick={abrirNovo}>
            <Plus className="h-4 w-4" />
            Novo Lançamento
          </Button>
        )}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Caixa</Label>
              <Select value={caixaSel} onValueChange={setCaixaSel}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TODOS}>Todos os caixas</SelectItem>
                  {caixasDisponiveis.map((id) => (
                    <SelectItem key={id} value={String(id)}>Caixa #{id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Início</Label>
              <Input
                type="date"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Fim</Label>
              <Input
                type="date"
                value={fim}
                onChange={(e) => setFim(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por descrição ou histórico..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Wallet className="h-4 w-4" />
              Saldo Atual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${saldoExibido >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {saldoExibido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{saldoLabel}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Total Créditos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {totalCreditos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {lancamentosFiltrados.filter((l) => l.debitoCredito === 'C').length} entrada(s)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              Total Débitos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {totalDebitos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {lancamentosFiltrados.filter((l) => l.debitoCredito === 'D').length} saída(s)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Conciliados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{totalConciliados}</div>
            <p className="text-xs text-muted-foreground mt-1">
              de {lancamentosFiltrados.length} lançamentos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle>Lançamentos do Período</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Histórico</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="text-right">Saldo Após</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : lancamentosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Nenhum lançamento encontrado no período
                    </TableCell>
                  </TableRow>
                ) : (
                  lancamentosFiltrados.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(l.dtMovimento).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell className="font-medium max-w-48 truncate" title={l.descricao}>
                        {l.descricao}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-40 truncate" title={l.historico}>
                        {l.historico || '—'}
                      </TableCell>
                      <TableCell>
                        {l.debitoCredito === 'C' ? (
                          <Badge className="bg-green-500/10 text-green-500 gap-1">
                            <ArrowUpCircle className="h-3 w-3" /> Crédito
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/10 text-red-500 gap-1">
                            <ArrowDownCircle className="h-3 w-3" /> Débito
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${l.debitoCredito === 'C' ? 'text-green-500' : 'text-red-500'}`}>
                        {l.debitoCredito === 'D' ? '- ' : '+ '}
                        {l.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {l.saldoAtual != null
                          ? l.saldoAtual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {l.isConciliado ? (
                          <Badge className="bg-green-500/10 text-green-500 gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Conciliado
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-500/10 text-yellow-500 gap-1">
                            <Clock className="h-3 w-3" /> Pendente
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog novo lançamento */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Novo Lançamento — Caixa #{formCaixaId}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Caixa *</Label>
              <Input
                type="number"
                min={1}
                value={formCaixaId}
                onChange={(e) => setFormCaixaId(e.target.value)}
                placeholder="Nº do caixa"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Tipo *</Label>
                <Select value={debitoCredito} onValueChange={(v) => setDebitoCredito(v as 'C' | 'D')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="C">Crédito (Entrada)</SelectItem>
                    <SelectItem value="D">Débito (Saída)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Pag / Rec *</Label>
                <Select value={pagRec} onValueChange={(v) => setPagRec(v as 'P' | 'R')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="R">Recebimento</SelectItem>
                    <SelectItem value="P">Pagamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Descrição *</Label>
              <Input
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descrição do lançamento"
                maxLength={200}
              />
            </div>

            <div className="space-y-1">
              <Label>Valor (R$) *</Label>
              <Input
                value={valorStr}
                onChange={(e) => setValorStr(e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
              />
            </div>

            <div className="space-y-1">
              <Label>Histórico</Label>
              <Textarea
                value={historico}
                onChange={(e) => setHistorico(e.target.value)}
                placeholder="Informações complementares..."
                rows={2}
                maxLength={500}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={registrar.isPending || !descricao.trim() || !valorStr}
            >
              {registrar.isPending ? 'Registrando...' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Caixa;
